import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Phone,
  ArrowLeft,
  ListChecks,
  Users,
  BarChart3,
  CalendarDays,
  Check,
  Mail,
  Sparkles,
  Hash,
  Mic,
  Volume2,
  ListTree,
  PhoneForwarded,
  UsersRound,
  FileSpreadsheet,
  Sheet,
  Moon,
  Sun
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useSession } from '@/hooks/useSession';

const SUPPORT_EMAIL = 'esaycall@abaye.co';
const HERO_BARS = [42, 60, 36, 72, 50, 84, 58, 46, 76, 54, 92, 64, 48, 80];

function ThemeToggle() {
  const [theme, setTheme] = useState<'light' | 'dark'>(() =>
    document.documentElement.classList.contains('dark') ? 'dark' : 'light'
  );
  useEffect(() => {
    if (theme === 'dark') document.documentElement.classList.add('dark');
    else document.documentElement.classList.remove('dark');
    localStorage.setItem('easy_call_theme', theme);
  }, [theme]);
  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
      aria-label="החלף ערכת נושא"
    >
      {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
    </Button>
  );
}

function AnimatedDashboard() {
  return (
    <div className="lp-float relative w-full max-w-md rounded-2xl border bg-background/80 p-4 shadow-2xl shadow-primary/10 backdrop-blur-sm">
      <div className="mb-4 flex items-center gap-1.5">
        <span className="size-2.5 rounded-full bg-destructive/60" />
        <span className="size-2.5 rounded-full bg-amber-400/70" />
        <span className="size-2.5 rounded-full bg-emerald-400/70" />
        <span className="ms-3 text-[10px] font-medium tracking-wide text-muted-foreground">
          Easy Call · דשבורד
        </span>
      </div>

      <div className="mb-4 grid grid-cols-3 gap-2">
        {[
          { label: 'רשומות החודש', value: '1,284' },
          { label: 'לקוחות', value: '3,902' },
          { label: 'סכום החודש', value: '47,120' }
        ].map((k) => (
          <div key={k.label} className="rounded-lg border bg-muted/40 p-2">
            <div className="text-[9px] text-muted-foreground">{k.label}</div>
            <div className="text-sm font-semibold tabular-nums">{k.value}</div>
          </div>
        ))}
      </div>

      <div className="mb-4 flex h-28 items-end gap-1 rounded-lg border bg-muted/30 p-2">
        {HERO_BARS.map((h, i) => (
          <div
            key={i}
            className="lp-bob flex-1 rounded-sm bg-gradient-to-t from-primary/50 to-primary"
            style={{ height: `${h}%`, animationDelay: `${i * 0.12}s` }}
          />
        ))}
      </div>

      <div className="space-y-2">
        {[
          { name: 'משה כהן', val: '120' },
          { name: 'שרה לוי', val: '85' },
          { name: 'דוד אברהם', val: '64' }
        ].map((r, i) => (
          <div
            key={r.name}
            className="lp-pulse flex items-center justify-between rounded-md border bg-muted/30 px-3 py-1.5 text-xs"
            style={{ animationDelay: `${i * 0.3}s` }}
          >
            <span className="text-muted-foreground">{r.name}</span>
            <span className="font-semibold tabular-nums">{r.val}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

interface Feature {
  icon: React.ElementType;
  title: string;
  body: string;
}

const FEATURES: Feature[] = [
  {
    icon: Phone,
    title: 'חיבור פשוט לימות המשיח',
    body: 'מדביקים קוד מוכן בשלוחת API פעם אחת, והמערכת מתחילה לקלוט שיחות אוטומטית - בלי מוקד אנושי.'
  },
  {
    icon: ListChecks,
    title: 'שאלונים מותאמים אישית',
    body: 'בונים רצף שאלות בעברית - הקשה (DTMF), דיבור (STT) או השמעה - עם אישור קלט אופציונלי לכל שאלה.'
  },
  {
    icon: ListTree,
    title: 'תפריט שלוחה חכם',
    body: 'הופכים את התוכנה לשלוחת תפריט: כל מקש מפנה לשאלון או לשלוחה אחרת במערכת, עם הודעה אוטומטית או מותאמת.'
  },
  {
    icon: PhoneForwarded,
    title: 'ניתוב לאחר השאלון',
    body: 'בסיום השאלון אפשר לנתק, לחזור לשלוחה הראשית או להעביר לשלוחה ספציפית - כלל-ארגוני או לכל שאלון בנפרד.'
  },
  {
    icon: Users,
    title: 'ניהול לקוחות מרכזי',
    body: 'הוספה ידנית, ייבוא מאקסל, וזיהוי אוטומטי לפי מספר הטלפון בעת השיחה - כולל ברכה בשם הלקוח.'
  },
  {
    icon: BarChart3,
    title: 'דשבורד וייצוא לאקסל',
    body: 'גרפים חודשיים וסכומים, וכל טבלה ניתנת לייצוא לאקסל מותאם-עברית עם חותמת זמן וכותרת קפואה.'
  },
  {
    icon: Sheet,
    title: 'סנכרון חי לגוגל שיטס',
    body: 'כל רשומה חדשה נכתבת מיד לגיליון גוגל שיטס שלכם - מוכנה לסינון, נוסחאות וטבלאות ציר, בלי ייצוא ידני.'
  },
  {
    icon: CalendarDays,
    title: 'מחזורי איסוף גמישים',
    body: 'יומי, שבועי, חודשי לועזי, חודשי עברי, ידני, או ללא הגבלה - עם מניעת כפילויות במחזור.'
  },
  {
    icon: UsersRound,
    title: 'ריבוי מנהלים לארגון',
    body: 'מצרפים כמה כתובות Google להתחברות לאותו ארגון, ומנהלים אותן ישירות מתוך הגדרות הארגון.'
  }
];

const STEPS = [
  {
    num: '1',
    icon: ListChecks,
    title: 'מגדירים שאלון',
    body: 'בונים את רצף השאלות בעברית, בוחרים סוג קלט לכל שאלה, ומגדירים מחזור איסוף.'
  },
  {
    num: '2',
    icon: Phone,
    title: 'מחברים לימות',
    body: 'מעתיקים את קוד שלוחת ה-API מעמוד ההגדרות ומדביקים בשלוחה בימות המשיח. זהו.'
  },
  {
    num: '3',
    icon: BarChart3,
    title: 'אוספים ומנתחים',
    body: 'הלקוחות מחייגים, הנתונים נכנסים לבד ומסתנכרנים חי לגוגל שיטס - ואתם רואים הכול בדשבורד ומייצאים לאקסל בלחיצה.'
  }
];

const INPUT_TYPES = [
  { icon: Hash, title: 'הקשה', body: 'קלט DTMF - מספרים, עם הגדרת אורך וספרות מותרות.' },
  { icon: Mic, title: 'דיבור', body: 'קלט קולי בהמרה לטקסט (STT) דרך ימות המשיח.' },
  { icon: Volume2, title: 'השמעה', body: 'הודעת טקסט בלבד - כותרת או הסבר, ללא קלט.' }
];

const PRICE_FEATURES = [
  'גישה מלאה לכל הכלים',
  'שאלונים ללא הגבלה, מחזורים ותפריט שלוחה',
  'ניהול לקוחות וייבוא מאקסל',
  'דשבורד וייצוא לאקסל',
  'ריבוי מנהלים לארגון',
  'גיבויים, עדכונים ותמיכה'
];

const FAQ = [
  {
    q: 'צריך לשנות משהו בהגדרות ימות המשיח?',
    a: 'לא. מעתיקים את קוד שלוחת ה-API מעמוד ההגדרות ומדביקים אותו בהגדרות המתקדמות של השלוחה. אין צורך בהתקנות.'
  },
  {
    q: 'איך הלקוחות מזוהים?',
    a: 'לפי מספר הטלפון. אם המספר מוכר - תושמע ברכה בשם הלקוח. אם לא - נוצר לקוח חדש אוטומטית בעת השיחה.'
  },
  {
    q: 'אפשר להשתמש בתוכנה רק כתפריט הפניות?',
    a: 'כן. אפשר להגדיר תפריט שלוחה שכל מקש בו מפנה לשאלון או לשלוחה אחרת במערכת - גם בלי שום שאלון.'
  },
  {
    q: 'אפשר לייצא את הנתונים?',
    a: 'בהחלט. כל טבלה במערכת ניתנת לייצוא לקובץ אקסל מותאם-עברית, עם חותמת זמן ושם הארגון.'
  }
];

export default function Landing() {
  const session = useSession();
  const loggedIn = !session.loading && !!session.user;
  const primaryHref = loggedIn ? '/dashboard' : '/login';
  const primaryLabel = loggedIn ? 'כניסה לפורטל' : 'התחברות';
  const ctaLabel = loggedIn ? 'כניסה לפורטל' : 'התחילו ניסיון חינם';

  return (
    <div className="min-h-screen bg-background text-foreground" dir="rtl">
      {/* nav */}
      <header className="sticky top-0 z-50 border-b bg-background/80 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-2">
            <div className="grid size-9 place-items-center rounded-lg bg-primary/10 text-primary">
              <Phone className="size-5" />
            </div>
            <div className="leading-tight">
              <div className="font-heading text-sm font-semibold">Easy Call</div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                איסוף נתונים
              </div>
            </div>
          </div>

          <nav className="hidden items-center gap-1 text-sm md:flex">
            <a href="#features" className="rounded-md px-3 py-2 text-muted-foreground transition-colors hover:text-foreground">
              תכונות
            </a>
            <a href="#how" className="rounded-md px-3 py-2 text-muted-foreground transition-colors hover:text-foreground">
              איך זה עובד
            </a>
            <a href="#pricing" className="rounded-md px-3 py-2 text-muted-foreground transition-colors hover:text-foreground">
              מחירים
            </a>
            <a href="#support" className="rounded-md px-3 py-2 text-muted-foreground transition-colors hover:text-foreground">
              תמיכה
            </a>
          </nav>

          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Button asChild size="sm">
              <Link to={primaryHref}>{primaryLabel}</Link>
            </Button>
          </div>
        </div>
      </header>

      {/* hero */}
      <section className="relative overflow-hidden">
        <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
          <div className="lp-float absolute -top-24 start-1/4 size-72 rounded-full bg-primary/20 blur-3xl" />
          <div
            className="lp-float absolute top-32 end-0 size-80 rounded-full bg-secondary/20 blur-3xl"
            style={{ animationDelay: '2s' }}
          />
        </div>

        <div className="mx-auto grid max-w-6xl items-center gap-12 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:py-24">
          <div className="lp-rise space-y-6 text-center lg:text-start">
            <span className="inline-flex items-center gap-1.5 rounded-full border bg-muted/40 px-3 py-1 text-xs text-muted-foreground">
              <Sparkles className="size-3 text-primary" />
              איסוף נתונים אוטומטי דרך הטלפון
            </span>
            <h1 className="font-heading text-4xl font-bold tracking-tight sm:text-5xl">
              כל הנתונים מהקו שלך -
              <span className="bg-gradient-to-l from-primary to-secondary bg-clip-text text-transparent">
                {' '}נאספים לבד, מסודרים בדשבורד
              </span>
            </h1>
            <p className="mx-auto max-w-xl text-base text-muted-foreground sm:text-lg lg:mx-0">
              Easy Call הופך כל שיחת טלפון לרשומת נתונים. הלקוח מחייג, המערכת מזהה אותו לפי המספר,
              שואלת את השאלות שהגדרת - והתשובות נשמרות מסודרות, מוכנות לייצוא. בלי טפסים, בלי מוקד.
            </p>
            <div className="flex flex-col items-center justify-center gap-3 sm:flex-row lg:justify-start">
              <Button asChild size="lg" className="gap-2">
                <Link to={primaryHref}>
                  {ctaLabel}
                  <ArrowLeft className="size-4" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline">
                <a href="#how">איך זה עובד?</a>
              </Button>
            </div>
            <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground lg:justify-start">
              <span className="inline-flex items-center gap-1">
                <Check className="size-3.5 text-primary" /> 72 שעות ניסיון חינם
              </span>
              <span className="inline-flex items-center gap-1">
                <Check className="size-3.5 text-primary" /> ללא כרטיס אשראי
              </span>
            </div>
          </div>

          <div className="flex justify-center lg:justify-end">
            <AnimatedDashboard />
          </div>
        </div>
      </section>

      {/* stats */}
      <section className="border-y bg-muted/30">
        <div className="mx-auto grid max-w-6xl grid-cols-2 gap-6 px-4 py-10 sm:px-6 md:grid-cols-4">
          {[
            { icon: Phone, label: 'חיבור', value: 'הדבקת קוד' },
            { icon: Users, label: 'זיהוי לקוח', value: 'לפי טלפון' },
            { icon: FileSpreadsheet, label: 'ייצוא', value: 'אקסל + שיטס' },
            { icon: CalendarDays, label: 'מחזורים', value: 'גמישים' }
          ].map((s) => (
            <div key={s.label} className="flex flex-col items-center gap-1 text-center">
              <s.icon className="size-5 text-primary" />
              <div className="text-lg font-semibold">{s.value}</div>
              <div className="text-xs text-muted-foreground">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* features */}
      <section id="features" className="scroll-mt-20">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:py-24">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="font-heading text-3xl font-bold tracking-tight sm:text-4xl">
              הרבה יותר מ"שאלון בטלפון"
            </h2>
            <p className="mt-4 text-muted-foreground">
              כל מה שצריך כדי לאסוף, לנתב ולנתח נתונים מהקו - במקום אחד.
            </p>
          </div>

          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((f) => (
              <div
                key={f.title}
                className="group rounded-2xl border bg-card p-6 transition-all hover:-translate-y-1 hover:border-primary/40 hover:shadow-lg hover:shadow-primary/5"
              >
                <div className="mb-4 grid size-11 place-items-center rounded-xl bg-primary/10 text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                  <f.icon className="size-5" />
                </div>
                <h3 className="mb-2 text-lg font-semibold">{f.title}</h3>
                <p className="text-sm leading-relaxed text-muted-foreground">{f.body}</p>
              </div>
            ))}
          </div>

          {/* input types */}
          <div className="mt-10 grid gap-4 sm:grid-cols-3">
            {INPUT_TYPES.map((t) => (
              <div key={t.title} className="rounded-xl border bg-muted/20 p-4">
                <div className="mb-1 flex items-center gap-2">
                  <t.icon className="size-4 text-primary" />
                  <strong className="text-sm">{t.title}</strong>
                </div>
                <p className="text-xs text-muted-foreground">{t.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* how it works */}
      <section id="how" className="scroll-mt-20 border-y bg-muted/30">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:py-24">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="font-heading text-3xl font-bold tracking-tight sm:text-4xl">
              שלושה צעדים, בלי כאב ראש
            </h2>
            <p className="mt-4 text-muted-foreground">מההגדרה הראשונה ועד הרשומה הראשונה - דקות ספורות.</p>
          </div>

          <div className="relative mt-14 grid gap-8 md:grid-cols-3">
            <div
              aria-hidden
              className="absolute inset-x-0 top-7 hidden h-px bg-gradient-to-l from-transparent via-border to-transparent md:block"
            />
            {STEPS.map((s) => (
              <div key={s.num} className="relative flex flex-col items-center text-center">
                <div className="grid size-14 place-items-center rounded-2xl border bg-background shadow-sm">
                  <s.icon className="size-6 text-primary" />
                </div>
                <div className="mt-4 inline-flex size-6 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
                  {s.num}
                </div>
                <h3 className="mt-3 text-lg font-semibold">{s.title}</h3>
                <p className="mt-2 max-w-xs text-sm leading-relaxed text-muted-foreground">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* pricing */}
      <section id="pricing" className="scroll-mt-20">
        <div className="mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:py-24">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="font-heading text-3xl font-bold tracking-tight sm:text-4xl">מחיר פשוט</h2>
            <p className="mt-4 text-muted-foreground">
              מתחילים ב-72 שעות ניסיון חינם. אחר כך - מחיר אחד וברור.
            </p>
          </div>

          <div className="mx-auto mt-12 max-w-lg rounded-2xl border border-primary shadow-md ring-1 ring-primary">
            <div className="rounded-t-2xl bg-primary/5 px-6 py-3 text-center text-sm font-medium text-primary">
              72 שעות ניסיון חינם - ללא כרטיס אשראי
            </div>
            <div className="p-6">
              <div className="flex flex-wrap items-baseline justify-center gap-x-2 gap-y-1 text-center">
                <span className="font-heading text-4xl font-bold tracking-tight">36 ₪</span>
                <span className="text-sm text-muted-foreground">לחודש</span>
              </div>
              <p className="mt-3 text-center text-sm text-muted-foreground">
                מחיר קבוע לחודש, ללא הגבלת שאלונים - כולל אחזקה, גיבויים, עדכונים ותמיכה.
              </p>
              <ul className="mx-auto mt-6 max-w-sm space-y-2.5 text-sm">
                {PRICE_FEATURES.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Check className="mt-0.5 size-4 shrink-0 text-primary" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <Button asChild size="lg" className="mt-6 w-full gap-2">
                <Link to={primaryHref}>
                  {ctaLabel}
                  <ArrowLeft className="size-4" />
                </Link>
              </Button>
              <p className="mt-3 text-center text-xs text-muted-foreground">
                הרכישה וההפעלה מתואמות מול שירות הלקוחות (<span className="font-mono">{SUPPORT_EMAIL}</span>).
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* support */}
      <section id="support" className="scroll-mt-20 border-y bg-muted/30">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:py-24">
          <div className="grid gap-12 lg:grid-cols-[1fr_1.2fr]">
            <div>
              <h2 className="font-heading text-3xl font-bold tracking-tight sm:text-4xl">כאן בשבילכם</h2>
              <p className="mt-4 text-muted-foreground">
                יש שאלה, צריך עזרה בהגדרה, או רוצים לרכוש? נשמח לעזור.
              </p>
              <div className="mt-6 space-y-3">
                <a
                  href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent('פנייה - Easy Call')}`}
                  className="inline-flex items-center gap-2 rounded-lg border bg-background px-4 py-3 text-sm transition-colors hover:border-primary/40 hover:bg-muted/40"
                >
                  <Mail className="size-4 text-primary" />
                  <span className="font-mono">{SUPPORT_EMAIL}</span>
                </a>
              </div>
            </div>

            <div className="space-y-3">
              {FAQ.map((item) => (
                <div key={item.q} className="rounded-xl border bg-card p-5">
                  <h3 className="mb-1.5 font-medium">{item.q}</h3>
                  <p className="text-sm leading-relaxed text-muted-foreground">{item.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* final CTA */}
      <section className="border-t bg-gradient-to-b from-primary/5 to-background">
        <div className="mx-auto max-w-3xl px-4 py-20 text-center sm:px-6">
          <h2 className="font-heading text-3xl font-bold tracking-tight sm:text-4xl">
            מוכנים לאסוף נתונים בלי מאמץ?
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-muted-foreground">
            הגדירו שאלון, הדביקו את הקוד בימות המשיח - והנתונים מתחילים להיכנס לבד.
          </p>
          <div className="mt-8">
            <Button asChild size="lg" className="gap-2">
              <Link to={primaryHref}>
                {ctaLabel}
                <ArrowLeft className="size-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* footer */}
      <footer className="border-t">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 px-4 py-8 text-sm sm:flex-row sm:px-6">
          <div className="flex items-center gap-2">
            <div className="grid size-7 place-items-center rounded-md bg-primary/10 text-primary">
              <Phone className="size-4" />
            </div>
            <span className="text-muted-foreground">Easy Call · מבית abaye</span>
          </div>
          <div className="flex items-center gap-4 text-muted-foreground">
            <a href="#features" className="hover:text-foreground">תכונות</a>
            <a href="#pricing" className="hover:text-foreground">מחירים</a>
            <a href={`mailto:${SUPPORT_EMAIL}`} className="hover:text-foreground">תמיכה</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
